/**
 * サイドナビ・ポータルのメニュー定義（単一の情報源）。
 *
 * - `path` は年度配下の相対パス（`/years/:year/` の後ろ）。
 * - `roles` を省略すると全ロールに表示。グループの roles と項目の roles は両方満たす必要がある。
 * - `status: 'planned'` は未実装（ナビでは「準備中」表示、遷移先は準備中ページ）。
 *   画面を実装したら features/<domain>/routes.tsx を作成し、ここの status を外す。
 * - kintone 版ポータル（fishHome / countryHome）のボタン構成を機能グループ別に再編している。
 */
import type { LucideIcon } from 'lucide-react';
import {
  Anchor,
  BookOpen,
  CheckCheck,
  Database,
  Megaphone,
  ScrollText,
  CalendarClock,
  CalendarRange,
  ClipboardList,
  FileSpreadsheet,
  Fish,
  Fuel,
  HandCoins,
  Map as MapIcon,
  Radar,
  ReceiptText,
  RefreshCcw,
  Send,
  Ship,
  Siren,
  Users,
  Wallet,
  Wrench,
  Binoculars,
  Files,
  Stamp,
  Network,
  MonitorCheck,
  ShieldCheck,
} from 'lucide-react';
import { hasRole, type AuthUser, type OrganizationType } from '@/auth/types';

export interface NavItem {
  id: string;
  label: string;
  /** 年度配下の相対パス（例: 'admin/users'） */
  path: string;
  icon: LucideIcon;
  /** ポータルのタイルに出す説明 */
  description?: string;
  roles?: readonly OrganizationType[];
  status?: 'planned';
  /** ポータルのタイルに表示しない */
  hideOnPortal?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  /** 海図の区画番号に見立てた識別コード（装飾） */
  code: string;
  roles?: readonly OrganizationType[];
  items: NavItem[];
}

const FUND_COOP: readonly OrganizationType[] = ['基金', '漁協'];
const FUND_ONLY: readonly OrganizationType[] = ['基金'];

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'daily',
    label: '日々の報告',
    code: 'A',
    items: [
      { id: 'advance-notices', label: '事前連絡', path: 'advance-notices', icon: Send, description: '出港予定の登録・編集（定時・速報の起点）' },
      { id: 'regular-reports', label: '定時連絡', path: 'fixed-reports', icon: CalendarClock, description: '正午の定時連絡の検索・編集' },
      { id: 'flash-reports', label: '速報', path: 'voyage-reports', icon: Siren, description: '調査・監視報告書（速報）の検索・編集' },
      { id: 'catch-surveys', label: '漁獲調査', path: 'fishing-grounds', icon: Fish, description: '漁場情報・水揚実績の登録' },
    ],
  },
  {
    id: 'expenses',
    label: '経費・支払',
    code: 'B',
    roles: FUND_COOP,
    items: [
      { id: 'fuel-costs', label: '燃料費', path: 'fuel-expenses', icon: Fuel, description: '速報に紐づく油代の登録' },
      { id: 'invoices', label: '請求（漁業者→漁業協同組合）', path: 'invoices', icon: ReceiptText, description: '監視経費 報告書兼請求書' },
      { id: 'receipts', label: '支払い（漁業協同組合→漁業者）', path: 'receipts', icon: Wallet, description: '監視経費 領収書' },
      { id: 'subsidy-payments', label: '助成金支払日登録', path: 'subsidy-payments', icon: HandCoins, description: '助成金の支払日を登録' },
    ],
  },
  {
    id: 'vessels',
    label: '船舶・計画',
    code: 'C',
    items: [
      { id: 'vessel-registrations', label: '参加船名簿', path: 'vessels', icon: Ship, description: '参加船の登録・承認', roles: FUND_COOP },
      { id: 'vessel-bulk-approval', label: '参加船名簿 一括承認', path: 'vessels/bulk-approval', icon: CheckCheck, description: '事業実施者ごとの承認期間を一括設定', roles: FUND_ONLY, hideOnPortal: true },
      { id: 'planned-days', label: '計画日数登録', path: 'vessels/plan-days', icon: CalendarRange, description: '事業実施者別の計画日数', roles: FUND_COOP },
      { id: 'equipment', label: '機器管理', path: 'equipment', icon: Radar, description: '貸与機器の管理番号・所在', roles: FUND_COOP },
    ],
  },
  {
    id: 'sea',
    label: '海域情報',
    code: 'D',
    items: [
      { id: 'sea-area-map', label: '海域図', path: 'sea-map', icon: MapIcon, description: '漁区番号別の事前連絡・実績' },
      { id: 'sightings', label: '視認・漁具情報', path: 'sea-map/sightings', icon: Binoculars, description: '外国漁船の視認・漁具の分布' },
    ],
  },
  {
    id: 'forms',
    label: '帳票出力（各種様式）',
    code: 'E',
    items: [
      { id: 'form-cost-summary', label: '統括表・明細表', path: 'forms/cost-summary', icon: FileSpreadsheet, roles: FUND_COOP },
      { id: 'form-mount-sheets', label: '台紙作成', path: 'forms/receipt-mounts', icon: Files, roles: FUND_COOP },
      { id: 'form-dispatch-plan', label: '調査船出動計画表', path: 'forms/dispatch-plan', icon: Anchor, roles: FUND_COOP },
      { id: 'form-vessel-roster', label: '参加船名簿', path: 'forms/roster', icon: ClipboardList },
      { id: 'form-monitoring-1', label: '監視実績報告書1', path: 'forms/monitoring-report-1', icon: Stamp },
      { id: 'form-monitoring-2', label: '監視実績報告書2', path: 'forms/monitoring-report-2', icon: Stamp },
      { id: 'form-monitoring-3', label: '監視実績報告書3', path: 'forms/monitoring-report-3', icon: Stamp, roles: FUND_COOP },
      { id: 'report-jobs', label: '帳票一括作成ジョブ', path: 'report-jobs', icon: Files, description: '一括作成した帳票（ZIP）の状況・ダウンロード' },
    ],
  },
  {
    id: 'admin',
    label: 'システム管理',
    code: 'F',
    roles: FUND_ONLY,
    items: [
      { id: 'admin-users', label: 'ユーザー管理', path: 'admin/users', icon: Users, description: 'ログインユーザーと組織区分の管理' },
      { id: 'annual-update', label: '年次更新処理', path: 'admin/annual-update', icon: RefreshCcw, description: '新年度の作成とマスタ引継ぎ' },
      { id: 'admin-masters', label: 'マスタ管理', path: 'admin/masters', icon: Database, description: '事業実施者・用船料・賃金・農林漁区・税率などのマスタ' },
      { id: 'admin-announcements', label: 'お知らせ管理', path: 'admin/announcements', icon: Megaphone, description: 'ポータルに表示するお知らせ' },
      { id: 'admin-manuals', label: 'マニュアル管理', path: 'admin/manuals', icon: BookOpen, description: '操作マニュアルの版管理' },
      { id: 'admin-audit-logs', label: '操作ログ', path: 'admin/audit-logs', icon: ScrollText, description: 'ログイン・更新・出力などの操作履歴' },
      { id: 'admin-ip-rules', label: 'IPアドレス制限', path: 'admin/ip-rules', icon: Network, description: '組織ごとに利用を許可する接続元 IP アドレス' },
      { id: 'admin-device-certificates', label: '端末証明書', path: 'admin/device-certificates', icon: MonitorCheck, description: '利用できる端末（クライアント証明書）の登録・失効' },
    ],
  },
];

/** サイドナビ下部の補助リンク */
export const NAV_FOOTER_ITEMS: NavItem[] = [
  { id: 'manual', label: '操作マニュアル', path: 'manual', icon: BookOpen },
  { id: 'account-security', label: '2段階認証の設定', path: 'account/security', icon: ShieldCheck, hideOnPortal: true },
  { id: 'settings', label: '環境設定', path: 'settings', icon: Wrench, roles: FUND_ONLY, status: 'planned', hideOnPortal: true },
];

/** ロールで絞り込んだグループ（空グループは除外） */
export function visibleNavGroups(user: AuthUser | null | undefined, groups: NavGroup[] = NAV_GROUPS): NavGroup[] {
  return groups
    .filter((g) => hasRole(user, g.roles))
    .map((g) => ({ ...g, items: g.items.filter((i) => hasRole(user, i.roles)) }))
    .filter((g) => g.items.length > 0);
}

/** 相対パスからナビ項目を探す（準備中ページ・パンくずで使用） */
export function findNavItem(path: string): { group?: NavGroup; item: NavItem } | null {
  const p = path.replace(/^\/+|\/+$/g, '');
  for (const g of NAV_GROUPS) {
    const item = g.items.find((i) => i.path === p);
    if (item) return { group: g, item };
  }
  const f = NAV_FOOTER_ITEMS.find((i) => i.path === p);
  return f ? { item: f } : null;
}
